import { useState } from 'react';
import { AtSign, Check, Copy, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SITE } from '@/lib/site';

function CopyChip({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — ignore.
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="group inline-flex max-w-full items-center gap-2 rounded-full border border-border bg-card px-4 py-2 font-mono text-xs text-muted-foreground transition-colors hover:border-bitcoin/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:text-sm"
      aria-label={`Copy ${label}`}
      title={`Copy ${label}`}
    >
      {copied ? (
        <Check className="size-3.5 shrink-0 text-green-500" />
      ) : (
        <Copy className="size-3.5 shrink-0" />
      )}
      <span className="truncate">{value}</span>
    </button>
  );
}

export function Footer() {
  return (
    <footer id="contact" className="scroll-mt-20 border-t border-border/60">
      <div className="container py-16 md:py-20">
        <div className="mx-auto max-w-2xl space-y-10 text-center">
          <div className="space-y-4">
            <p className="font-mono text-sm text-nostr">// say hello</p>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Let's build the open web together
            </h2>
            <p className="mx-auto max-w-md text-muted-foreground">
              The fastest way to reach me is Nostr. Zaps welcome — they keep the servers on and the coffee flowing.
            </p>
          </div>

          <div className="flex flex-col items-center gap-3">
            <CopyChip value={SITE.npub} label="npub" />
            <div className="flex flex-wrap items-center justify-center gap-3">
              <CopyChip value={SITE.nip05} label="NIP-05 address" />
              <CopyChip value={SITE.lightningAddress} label="Lightning address" />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild variant="outline" className="rounded-full">
              <a href={SITE.nostrProfileUrl} target="_blank" rel="noopener noreferrer">
                <AtSign className="size-4" />
                Follow on Nostr
              </a>
            </Button>
            <Button asChild className="rounded-full font-semibold">
              <a href={`lightning:${SITE.lightningAddress}`}>
                <Zap className="size-4 fill-current" />
                Zap me
              </a>
            </Button>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-border/60 pt-8 text-sm text-muted-foreground sm:flex-row">
          <p>
            © {new Date().getFullYear()} {SITE.name} · Nairobi, Kenya
          </p>
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors hover:text-bitcoin"
          >
            Vibed with Shakespeare
          </a>
        </div>
      </div>
    </footer>
  );
}
